import { ColumnDef } from 'features/common/hooks/useColumnSelection';

/**
 * The per-row Show column, shown ONLY while a scanner-grouped event lists its whole
 * festival (TIC-732). It lives in the headings arrays (so header/body agree on order)
 * but NOT in the column defs: visibility is derived from the grouped state by the page,
 * not user-toggled, so it never appears in the column picker.
 */
export const EVENT_COLUMN_KEY = 'customFields-responses-table-event';

// PAID column order matches the customer-facing Attendee List redesign (the order the venue-facing
// "Classic" view uses — TIC-308 corrected an earlier docs misunderstanding that had the green view
// leading with Order ID). Render order:
//   name → seat → contact (email) → when → amount → add-ons → ref → role → status → what bought →
//   did scan → promo → gift cards → [printer + ⋮ actions, sticky right].
// Custom-question columns append at the END for the Attendee List (isAttendeesList=true); the QR
// download column (when present) and the sticky actions cell trail every data column.
// The date key keeps its `-green` i18n suffix (the strict-gate key split) — a deliberate green delta.
export const PAID_COLUMN_HEADINGS = [
	'customFields-responses-table-fullName',
	EVENT_COLUMN_KEY,
	'customFields-responses-table-phone',
	// custom question columns spliced here at RESPONSES_INDEX = 6
	'customFields-responses-table-seat',
	'customFields-responses-table-email',
	'customFields-responses-table-date-green',
	'customFields-responses-table-amount',
	'customFields-responses-table-addons',
	'customFields-responses-table-orderId',
	'customFields-responses-table-role',
	'customFields-responses-table-status',
	'customFields-responses-table-ticketTypes',
	'customFields-responses-table-scanned',
	'customFields-responses-table-promotion',
	'customFields-responses-table-giftCards',
];

export const REGISTRATION_COLUMN_HEADINGS = [
	'customFields-responses-table-orderId',
	'customFields-responses-table-role',
	'customFields-responses-table-email',
	'customFields-responses-table-fullName',
	EVENT_COLUMN_KEY,
	'customFields-responses-table-phone',
	// custom question columns spliced here at RESPONSES_INDEX = 6
	'customFields-responses-table-date-green',
	'customFields-responses-table-ticketTypes',
	'customFields-responses-table-scanned',
	'customFields-responses-table-promotion',
	'customFields-responses-table-giftCards',
	'customFields-responses-table-addons',
	'customFields-responses-table-seat',
];

/** Priority-ordered column definitions for the paid (normal) event mode. Order mirrors PAID_COLUMN_HEADINGS. */
export const PAID_COLUMN_DEFS: ColumnDef[] = [
	{
		key: 'customFields-responses-table-fullName',
		labelKey: 'customFields-responses-table-fullName',
		minWidth: 200,
		priority: 1,
	},
	{
		key: 'customFields-responses-table-phone',
		labelKey: 'customFields-responses-table-phone',
		minWidth: 160,
		priority: 5,
		defaultHidden: true,
	},
	{
		key: 'customFields-responses-table-seat',
		labelKey: 'customFields-responses-table-seat',
		minWidth: 200,
		priority: 2,
	},
	{
		key: 'customFields-responses-table-email',
		labelKey: 'customFields-responses-table-email',
		minWidth: 240,
		priority: 3,
	},
	{
		key: 'customFields-responses-table-date-green',
		labelKey: 'customFields-responses-table-date-green',
		minWidth: 220,
		priority: 4,
	},
	{
		key: 'customFields-responses-table-amount',
		labelKey: 'customFields-responses-table-amount',
		minWidth: 120,
		priority: 5,
	},
	{
		key: 'customFields-responses-table-addons',
		labelKey: 'customFields-responses-table-addons',
		minWidth: 180,
		priority: 6,
	},
	{
		key: 'customFields-responses-table-orderId',
		labelKey: 'customFields-responses-table-orderId',
		minWidth: 100,
		priority: 7,
	},
	{
		key: 'customFields-responses-table-role',
		labelKey: 'customFields-responses-table-role',
		minWidth: 120,
		priority: 8,
	},
	{
		key: 'customFields-responses-table-status',
		labelKey: 'customFields-responses-table-status',
		minWidth: 120,
		priority: 9,
	},
	{
		key: 'customFields-responses-table-ticketTypes',
		labelKey: 'customFields-responses-table-ticketTypes',
		minWidth: 200,
		priority: 10,
	},
	{
		key: 'customFields-responses-table-scanned',
		labelKey: 'customFields-responses-table-scanned',
		minWidth: 120,
		priority: 11,
	},
	{
		key: 'customFields-responses-table-promotion',
		labelKey: 'customFields-responses-table-promotion',
		minWidth: 180,
		priority: 12,
	},
	{
		key: 'customFields-responses-table-giftCards',
		labelKey: 'customFields-responses-table-giftCards',
		minWidth: 180,
		priority: 13,
	},
];

/** Priority-ordered column definitions for the registration event mode. Order mirrors REGISTRATION_COLUMN_HEADINGS. */
export const REGISTRATION_COLUMN_DEFS: ColumnDef[] = [
	{
		key: 'customFields-responses-table-orderId',
		labelKey: 'customFields-responses-table-orderId',
		minWidth: 100,
		priority: 1,
	},
	{
		key: 'customFields-responses-table-role',
		labelKey: 'customFields-responses-table-role',
		minWidth: 120,
		priority: 2,
	},
	{
		key: 'customFields-responses-table-email',
		labelKey: 'customFields-responses-table-email',
		minWidth: 240,
		priority: 3,
	},
	{
		key: 'customFields-responses-table-fullName',
		labelKey: 'customFields-responses-table-fullName',
		minWidth: 200,
		priority: 4,
	},
	{
		key: 'customFields-responses-table-phone',
		labelKey: 'customFields-responses-table-phone',
		minWidth: 160,
		priority: 5,
		defaultHidden: true,
	},
	{
		key: 'customFields-responses-table-date-green',
		labelKey: 'customFields-responses-table-date-green',
		minWidth: 220,
		priority: 6,
	},
	{
		key: 'customFields-responses-table-ticketTypes',
		labelKey: 'customFields-responses-table-ticketTypes',
		minWidth: 200,
		priority: 7,
	},
	{
		key: 'customFields-responses-table-scanned',
		labelKey: 'customFields-responses-table-scanned',
		minWidth: 120,
		priority: 8,
	},
	{
		key: 'customFields-responses-table-promotion',
		labelKey: 'customFields-responses-table-promotion',
		minWidth: 180,
		priority: 9,
	},
	{
		key: 'customFields-responses-table-giftCards',
		labelKey: 'customFields-responses-table-giftCards',
		minWidth: 180,
		priority: 10,
	},
	{
		key: 'customFields-responses-table-addons',
		labelKey: 'customFields-responses-table-addons',
		minWidth: 180,
		priority: 11,
	},
	{
		key: 'customFields-responses-table-seat',
		labelKey: 'customFields-responses-table-seat',
		minWidth: 200,
		priority: 12,
	},
];

// Legacy — these arrays are no longer consumed by the Punchup table (which now
// auto-sizes via `useResizableColumns` + `max-content`). They remain because the
// dark-theme red-zone `ResponsesTable` still imports them by name. Do not extend
// or rely on them in new green-zone code.
export const COLUMN_MIN_WIDTH_HEADERS = [220, 200, 240, 160, 160, 160, 160, 160, 160, 180, 180, 180, 180, 200, 80];
export const COLUMN_MIN_WIDTH = [100, 150, 300, 250, 250, 120, 250, 120, 250, 120, 180, 180, 180, 50, 80];
export const COLUMN_MAX_WIDTH = [
	undefined,
	undefined,
	undefined,
	undefined,
	450,
	undefined,
	undefined,
	undefined,
	undefined,
	150,
	200,
	200,
	200,
	250,
	undefined,
	80,
];

// Custom question columns are spliced after the identity block (orderId, type, email, fullName,
// event, phone) — after phone at index 5 — so RESPONSES_INDEX = 6. (Phone was inserted after
// fullName, and the grouped-listing Show column after fullName again (TIC-732); the red
// customer-redesign order used 4 before either existed.)
export const RESPONSES_INDEX = 6;

// Custom field names injected by the system — not venue-manager questions.
// Filter these out before rendering question columns or flattening response rows.
export const SYSTEM_FIELD_NAMES = ['ATTENDEE_NAME', 'ATTENDEE_EMAIL'];
